import { useState } from 'react';
import { ClipboardList, Search, Loader2, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { tradingApi } from '../../services/tradingApi';
import type { RbacAuditLogEntry } from '../../types/trading';

const ACTION_OPTIONS = [
  { value: '', label: 'All actions' },
  { value: 'role_granted', label: 'role_granted' },
  { value: 'role_revoked', label: 'role_revoked' },
  { value: 'override_set', label: 'override_set' },
  { value: 'override_removed', label: 'override_removed' },
  { value: 'permission_added', label: 'permission_added' },
  { value: 'permission_removed', label: 'permission_removed' },
];

const ACTION_COLORS: Record<string, string> = {
  role_granted: 'bg-green-900/30 text-green-400',
  role_revoked: 'bg-red-900/30 text-red-400',
  override_set: 'bg-amber-900/30 text-amber-400',
  override_removed: 'bg-slate-700 text-slate-400',
  permission_added: 'bg-blue-900/30 text-blue-400',
  permission_removed: 'bg-red-900/30 text-red-400',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

export default function RbacAuditLog() {
  const [entries, setEntries] = useState<RbacAuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  // Filter form state
  const [actorEmail, setActorEmail] = useState('');
  const [targetEmail, setTargetEmail] = useState('');
  const [action, setAction] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const fetchPage = async (p: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await tradingApi.getRbacAuditLog({
        page: p,
        page_size: pageSize,
        actor_email: actorEmail || undefined,
        target_email: targetEmail || undefined,
        action: action || undefined,
        from_date: fromDate ? new Date(fromDate).toISOString() : undefined,
        to_date: toDate ? new Date(toDate).toISOString() : undefined,
      });
      setEntries(data.entries);
      setTotal(data.total);
      setPage(p);
      setSearched(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-teal-900/30 rounded-lg">
          <ClipboardList className="h-5 w-5 text-teal-400" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-white">RBAC Audit Log</h1>
          <p className="text-sm text-slate-400">All role and permission change events</p>
        </div>
      </div>

      {/* Filter form */}
      <form
        onSubmit={handleSearch}
        className="bg-slate-800 border border-slate-700 rounded-xl p-4 space-y-4"
      >
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Actor email</label>
            <input
              type="text"
              value={actorEmail}
              onChange={(e) => setActorEmail(e.target.value)}
              placeholder="admin@example.com"
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Target email</label>
            <input
              type="text"
              value={targetEmail}
              onChange={(e) => setTargetEmail(e.target.value)}
              placeholder="user@example.com"
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Action</label>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value)}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
            >
              {ACTION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-xs text-slate-400 mb-1">From</label>
              <input
                type="datetime-local"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-slate-400 mb-1">To</label>
              <input
                type="datetime-local"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </div>
          </div>
        </div>
        <button
          type="submit"
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <Search className="h-4 w-4" />
          Search
        </button>
      </form>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-800 rounded-lg text-red-400 text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Results */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 text-teal-400 animate-spin" />
        </div>
      ) : searched && entries.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <ClipboardList className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p>No audit events match your filters</p>
        </div>
      ) : entries.length > 0 ? (
        <>
          <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-700 flex justify-between items-center text-xs text-slate-400">
              <span>{total} total events</span>
              <span>Page {page}</span>
            </div>

            <div className="divide-y divide-slate-700/50">
              {entries.map((entry) => (
                <div key={entry.id} className="px-4 py-3 flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-mono font-medium ${
                          ACTION_COLORS[entry.action] ?? 'bg-slate-700 text-slate-300'
                        }`}
                      >
                        {entry.action}
                      </span>
                      <span className="text-slate-500 text-xs">{formatDate(entry.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                      <span>by <span className="text-slate-300">{entry.actor_email ?? 'system'}</span></span>
                      {entry.target_email && (
                        <span>→ <span className="text-slate-300">{entry.target_email}</span></span>
                      )}
                    </div>
                    {Object.keys(entry.details).length > 0 && (
                      <p className="mt-1 text-xs text-slate-500 font-mono truncate">
                        {JSON.stringify(entry.details)}
                      </p>
                    )}
                  </div>
                  <span className="text-slate-600 text-xs shrink-0">#{entry.id}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between text-sm text-slate-400">
            <span>
              {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => fetchPage(page - 1)}
                disabled={page === 1}
                className="p-2 rounded border border-slate-700 hover:bg-slate-700 disabled:opacity-40 transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => fetchPage(page + 1)}
                disabled={page * pageSize >= total}
                className="p-2 rounded border border-slate-700 hover:bg-slate-700 disabled:opacity-40 transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </>
      ) : !searched ? (
        <div className="text-center py-16 text-slate-500">
          <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p className="font-medium text-slate-400">Apply filters and click Search</p>
          <p className="text-xs mt-1">Leave all fields empty to load the full audit log</p>
        </div>
      ) : null}
    </div>
  );
}
